import { z } from "zod";

export const MAX_MESSAGE_LEN = 1000;

const ticketStateValue = z.enum(["aperto", "in-lavorazione", "chiuso"]);

export const adminTicketListRequestSchema = z.object({
  state: ticketStateValue.optional().nullable(),
  sortBy: z.enum(["createdAt", "updatedAt"]).optional().default("createdAt"),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
  page: z.number().int().min(0).optional().default(0),
  size: z.number().int().min(1).max(200).optional().default(20),
});

export const adminTicketListItemSchema = z.object({
  id: z.string(),
  subject: z.string(),
  state: ticketStateValue,
  createdAt: z.any().optional().nullable(),
  updatedAt: z.any().optional().nullable(),
  unreadCount: z.coerce.number().optional().nullable(),
  vendorId: z.coerce.number().optional().nullable(),
  vendorBusinessName: z.string().optional().nullable(),
  operatorId: z.coerce.number().optional().nullable(),
  assigneeUserName: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
});

export const adminTicketListResponseSchema = z.object({
  items: z.array(adminTicketListItemSchema),
  totalCount: z.coerce.number(),
  page: z.coerce.number().int(),
  size: z.coerce.number().int(),
});

export const adminTicketIdRequestSchema = z.object({
  ticketId: z.string().uuid(),
});

export const adminTicketAssignRequestSchema = z.object({
  ticketId: z.string().uuid(),
  operatorId: z.number().int().positive(),
});

export const adminTicketMessageSendRequestSchema = z.object({
  ticketId: z.string().uuid(),
  message: z.string().trim().max(MAX_MESSAGE_LEN).optional().nullable(),
  file: z.any().optional().nullable(),
});

export const adminTicketMessageListRequestSchema = z.object({
  ticketId: z.string().uuid(),
  page: z.number().int().min(0).optional().default(0),
});

export const adminTicketMessageItemSchema = z.object({
  id: z.string(),
  operator: z.boolean(),
  message: z.string().optional().nullable(),
  attachmentName: z.string().optional().nullable(),
  timestamp: z.any().optional().nullable(),
  editable: z.boolean().optional().default(false),
  read: z.boolean().optional().default(false),
});

export const adminTicketMessageListResponseSchema = z.object({
  items: z.array(adminTicketMessageItemSchema),
  totalCount: z.coerce.number(),
  page: z.coerce.number().int(),
  size: z.coerce.number().int(),
});

export const adminTicketMessageSendResponseSchema = z.object({
  messageId: z.string(),
});

export const adminTicketAttachmentRequestSchema = z.object({
  ticketId: z.string().uuid(),
  messageId: z.string().uuid(),
});

export const adminTicketAttachmentResponseSchema = z.object({
  attachmentName: z.string().optional().nullable(),
  attachmentBase64: z.string(),
});

export const adminTicketMessageEditRequestSchema = z.object({
  ticketId: z.string().uuid(),
  messageId: z.string().uuid(),
  action: z.enum(["edit", "delete"]),
  message: z.string().trim().max(MAX_MESSAGE_LEN).optional().nullable(),
  removeAttachment: z.boolean().optional(),
  file: z.any().optional().nullable(),
  existingAttachmentKept: z.boolean().optional(),
});

export const adminTicketMessagePollRequestSchema = z.object({
  ticketId: z.string().uuid(),
  afterTimestamp: z.any().optional().nullable(),
  ownMessageIds: z.array(z.string()).optional().default([]),
});

export const adminTicketSnapshotSchema = z.object({
  id: z.string(),
  state: ticketStateValue,
  updatedAt: z.any().optional().nullable(),
  unreadCount: z.coerce.number(),
});

export const adminTicketSummaryResponseSchema = z.object({
  menuBadgeCount: z.coerce.number(),
  pollingEnabled: z.boolean(),
  tickets: z.array(adminTicketSnapshotSchema).optional().default([]),
});

export const adminTicketMessagePollResponseSchema = z.object({
  items: z.array(adminTicketMessageItemSchema),
  readMessageIds: z.array(z.string()).optional().default([]),
});

export function validateAdminTicketListRequest(data) {
  return adminTicketListRequestSchema.parse(data ?? {});
}

export function parseAdminTicketListResponse(data) {
  return adminTicketListResponseSchema.parse(data);
}

export function validateAdminTicketAssignRequest(data) {
  return adminTicketAssignRequestSchema.parse(data);
}

export function validateAdminTicketIdRequest(data) {
  return adminTicketIdRequestSchema.parse(data);
}

export function validateAdminTicketMessageSendRequest(data) {
  const parsed = adminTicketMessageSendRequestSchema.parse(data);
  const hasMessage = Boolean(parsed.message?.trim());
  const hasFile = Boolean(parsed.file);
  if (!hasMessage && !hasFile) {
    throw new Error("Inserisci un messaggio o un allegato");
  }
  return parsed;
}

export function validateAdminTicketMessageListRequest(data) {
  return adminTicketMessageListRequestSchema.parse(data);
}

export function parseAdminTicketMessageListResponse(data) {
  return adminTicketMessageListResponseSchema.parse(data);
}

export function parseAdminTicketMessageSendResponse(data) {
  return adminTicketMessageSendResponseSchema.parse(data);
}

export function validateAdminTicketAttachmentRequest(data) {
  return adminTicketAttachmentRequestSchema.parse(data);
}

export function parseAdminTicketAttachmentResponse(data) {
  return adminTicketAttachmentResponseSchema.parse(data);
}

export function validateAdminTicketMessageEditRequest(data) {
  const parsed = adminTicketMessageEditRequestSchema.parse(data);
  if (parsed.action === "edit") {
    const hasMessage = Boolean(parsed.message?.trim());
    const hasFile = Boolean(parsed.file);
    const keepingAttachment = Boolean(parsed.existingAttachmentKept);
    if (!hasMessage && !hasFile && !keepingAttachment) {
      throw new Error("Inserisci un messaggio o un allegato");
    }
  }
  return parsed;
}

export function parseAdminTicketSummaryResponse(data) {
  return adminTicketSummaryResponseSchema.parse(data);
}

export function validateAdminTicketMessagePollRequest(data) {
  return adminTicketMessagePollRequestSchema.parse(data);
}

export function parseAdminTicketMessagePollResponse(data) {
  return adminTicketMessagePollResponseSchema.parse(data ?? { items: [] });
}

const assignableUserItemSchema = z.object({
  loginId: z.coerce.number(),
  userName: z.string(),
  email: z.string(),
});

const assignableUsersResponseSchema = z.object({
  items: z.array(assignableUserItemSchema),
});

export function parseAssignableUsersResponse(data) {
  return assignableUsersResponseSchema.parse(data ?? { items: [] });
}
 